import * as React from "react";
import { FormEvent } from "react";
import { Trans } from "react-i18next";
import { NavigateFunction, useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import {
  Form,
  FormField,
  Input,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  currentApiDomain,
  EMPTY_STRING,
  getServiceNameFromLocation,
  isEmptyString,
  REQUEST_INIT,
  HEADER_INIT,
  navigateToServices,
} from "../../../util/helperUtil";
import {
  ALPHA_NUMERIC_REGEX_EXP,
  INTERNAL_SERVER_ERROR_CODE_ONE,
  RECOGNIZED_RESPONSE_STATUS, RevokeBadgeResponse,
} from "../../../module/AuthAdminModule";
import ContentHeader from "../../common/ContentHeader";
import FormActions from "../../common/FormActions";
import ErrorNotification from "../../common/ErrorNotification";
import { useTranslator } from "../../../localization/Translator";
import { useOutletContext } from "react-router-dom";
import { SessionDetails } from "../../../module/AuthAdminModule";
import ResponsePage, {ResponsePageProps, ResponsePageType} from "../../common/ResponsePage";

const OPERATIONS_URL: string = "/services/name_maask/operations";
const REVOKE_BADGE_URL: string = `${currentApiDomain}/RevokeBadge`;
const TICKET_URL_PATTERN = /^(http:\/\/|https:\/\/)?(i|issues|t.corp)\.amazon\.com\/?(issues\/)?[-A-Za-z0-9]+$/;
const CARD_ID_INPUT_LENGTH = 14;

const RevokeBadgeForm = () => {
  const location = useLocation();
  const AUTHORIZE_URL = `${currentApiDomain}/Authorize`;
  const serviceName = getServiceNameFromLocation(location);
  const { csrfToken } = useOutletContext<SessionDetails>();
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [response, setResponse] = React.useState<ResponsePageProps | null>(null);
  const [cardId, setCardId] = React.useState(EMPTY_STRING);
  const [ticketUrl, setTicketUrl] = React.useState(EMPTY_STRING);
  const [cardIdError, setCardIdError] = React.useState(EMPTY_STRING);
  const [ticketUrlError, setTicketUrlError] = React.useState(EMPTY_STRING);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isInputInvalid, setIsInputInvalid] = React.useState(true);
  const [isSuccess, setIsSuccess] = React.useState(false);

  const t = useTranslator();
  const navigate: NavigateFunction = useNavigate();

  const validateCardId = (value: string) => {
    if (isEmptyString(value)) {
      setCardIdError(t.translate("error_message_required_field"));
      return false;
    }
    if (!ALPHA_NUMERIC_REGEX_EXP.test(value) || value.length !== CARD_ID_INPUT_LENGTH) {
      setCardIdError(t.translate("input_form_alpha_numeric_only_error_message", {length: CARD_ID_INPUT_LENGTH}));
      return false;
    }
    setCardIdError(EMPTY_STRING);
    return true;
  };

  const validateTicketUrl = (value: string) => {
    if (isEmptyString(value)) {
      setTicketUrlError(t.translate("error_message_required_field"));
      return false;
    }
    if(!value.match(TICKET_URL_PATTERN)) {
      setTicketUrlError(t.translate("error_message_invalid_ticket_url"));
      return false;
    }
    try {
      new URL(value);
      setTicketUrlError(EMPTY_STRING);
      return true;
    } catch (e) {
      setTicketUrlError(t.translate("error_message_invalid_url"));
      return false;
    }
  };

  const validateInputs = () => {
    const isCardIdValid = validateCardId(cardId);
    const isTicketUrlValid = validateTicketUrl(ticketUrl);
    setIsInputInvalid(!(isCardIdValid && isTicketUrlValid));
    return isCardIdValid && isTicketUrlValid;
  };

  const handleCardIdChange = (value: string) => {
    setCardIdError(EMPTY_STRING);
    setCardId(value);
    setIsInputInvalid(!validateCardId(value));
  };

  const handleTicketUrlChange = (value: string) => {
    setTicketUrlError(EMPTY_STRING);
    setTicketUrl(value);
    setIsInputInvalid(!validateTicketUrl(value));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorCode(null);
    if (!validateInputs()) return;
    setIsLoading(true);
    
    fetch(REVOKE_BADGE_URL, {
      method: "POST",
      ...REQUEST_INIT,
      headers: {
        ...HEADER_INIT,
        "anti-csrftoken-a2z": csrfToken,
      },
      body: JSON.stringify({
        serviceName: serviceName,
        cardIds: [cardId],
        justificationUrl: ticketUrl,
      }),
    })
    .then((res) => {
      setIsLoading(false);
      if (RECOGNIZED_RESPONSE_STATUS.includes(res.status)) {
        if (res.status === 401) {
          // redirect to authenticate
          window.location.replace(AUTHORIZE_URL);
          return res.json();
        }
        if (res.status === 200) {
          return res.json();
        }
      }
      setIsSuccess(false);
      throw new Error("Unrecognized response status " + res.status);
    })
    .then((res: any) => {
      res = res as RevokeBadgeResponse;
      if (res.success) {
        setErrorCode(null);
        setResponse({
          pageType: ResponsePageType.SUCCESS,
          message: t.translate("revoke_badge_success_message"),
          headerTitle: t.translate("revoke_badge_success_title"),
          buttonName: t.translate("return_to_home_page_button_name"),
          buttonOnClick: navigateToServices,
        });
        setIsSuccess(true);
        return;
      }
      console.log("Revoke badge failed", res);
      setErrorCode(String(res.responseCode));
      setIsSuccess(false);
      return;
    })
    .catch((error) => {
      setIsLoading(false);
      setIsSuccess(false);
      console.error("Error revoking badge:", error);
      if (error.errorId) {
        setErrorCode(error.errorId);
      } else {
        setErrorCode(INTERNAL_SERVER_ERROR_CODE_ONE);
      }
    });
  };

  const cardIdDescription = (
    <Trans>{t.translate("revoke_badge_card_id_description")}</Trans>
  );

  const ticketUrlDescription = (
    <Trans>{t.translate("revoke_badge_ticket_url_description")}</Trans>
  );

  if (isSuccess) {
    const responsePage = response && <ResponsePage
        message={response.message}
        buttonName={response.buttonName}
        buttonOnClick={response.buttonOnClick}
        buttonName2={response.buttonName2}
        buttonOnClick2={response.buttonOnClick2}
        headerTitle={response.headerTitle}
        pageType={response?.pageType}/>;
    return (
      <SpaceBetween size={"xxl"} alignItems={"center"}>
        {responsePage}
      </SpaceBetween>
    );
  }
  return (
    <SpaceBetween size={"xxl"} alignItems={"center"}>
      {errorCode && <ErrorNotification errorCode={errorCode} />}
      <ContentHeader title={t.translate("revoke_badge_header")} />
      <form onSubmit={(e) => handleSubmit(e)}>
        <Form
          className={"width-flex"}
          actions={
            <FormActions
              cancelButtonName={t.translate("cancel_button_name")}
              submitButtonName={t.translate("submit_button_name")}
              cancelOnClick={() => navigate(OPERATIONS_URL)}
              isLoading={isLoading}
              isInputInvalid={isInputInvalid}
            />
          }
        >
          <SpaceBetween direction="vertical" size="l">
            <FormField
              stretch
              label={t.translate("revoke_badge_card_id_label")}
              errorText={cardIdError}
              description={cardIdDescription}
            >
              <Input
                value={cardId}
                onChange={(event) => handleCardIdChange(event.detail.value)}
                placeholder={t.translate("revoke_badge_card_id_placeholder")}
              />
            </FormField>
            <FormField
              stretch
              label={t.translate("revoke_badge_ticket_url_label")}
              errorText={ticketUrlError}
              description={ticketUrlDescription}
            >
              <Input
                value={ticketUrl}
                onChange={(event) => handleTicketUrlChange(event.detail.value)}
                placeholder={t.translate("revoke_badge_ticket_url_placeholder")}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </form>
    </SpaceBetween>
  );
};

export default RevokeBadgeForm;
